export const namespaced = true
import GatewayPortalV2 from "@soenergy/frontend-library/src/services/api/GatewayPortalV2"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import {
  localStore,
  sessionStore,
} from "@soenergy/frontend-library/src/services/StorageFactory"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"
import dayjs from "dayjs"

import {
  EvFuelType,
  EvRegistrationCheckErrorMessages,
  MeterEligibilityError,
  MeterReadingFrequency,
  SmValidationDialogs,
} from "@/constants"
import {
  OTHER_OPTION_VALUE,
  SelectOption,
  SmartChargingCompatibleAsset,
  SmartChargingEligibilityOutcome,
} from "@/types/SmartCharging.types"
import {
  ConsentEntry,
  EnergyUsageEntry,
  EV_ELIGIBILITY_HH_SLOTS_PER_DAY,
  EV_ELIGIBILITY_REQUIRED_HH_READS,
  EV_ELIGIBILITY_WINDOW_END_HOURS_AGO,
  EV_ELIGIBILITY_WINDOW_START_HOURS_AGO,
  EV_SMART_CHARGING_ELIGIBILITY_TRACKING_EVENT,
  EV_SMART_HH_ELIGIBILITY_TRACKING_EVENT,
  EV_SMART_METER_ELIGIBILITY_TRACKING_EVENT,
  EV_SMART_VEHICLE_ELIGIBILITY_TRACKING_EVENT,
  EvEligibilityCheckState,
  countValidHHSlots,
  evNoHHDataState,
  isEvEligibilityBypassed,
} from "@/methods/evEligibilityHelpers"

export type { EvEligibilityCheckState }

type AssetCatalogueState = "idle" | "loading" | "loaded" | "error"

export const COMPATIBLE_ASSETS_STORAGE_KEY = "evCompatibleAssets"

const MAX_VEHICLE_REGISTRATION_LENGTH = 7

const OTHER_ASSET_NAME = "Other"
const NO_CHARGER_ASSET_NAME = "No charger"

const assetName = (asset: SmartChargingCompatibleAsset): string =>
  `${asset.oem.displayName} ${asset.displayName}`

const EV_SMART_WAITLIST_JOIN_TRACKING_EVENT = "renewalEvSmartWaitlistJoin"

export const WAITLIST_JOINS_STORAGE_KEY = "evSmartWaitlistJoins"

const getPersistedWaitlistJoins = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStore.getItem(WAITLIST_JOINS_STORAGE_KEY)) || {}
  } catch {
    localStore.removeItem(WAITLIST_JOINS_STORAGE_KEY)
    return {}
  }
}

const getDefaultFormState = () => {
  return {
    hasEv: null as null | boolean,
    isConsideringEv: null as null | boolean,
    hasEligibleVehicle: null as null | boolean,
    vehicleFuelType: null as null | keyof typeof EvFuelType,
    isMeterEligible: null as null | boolean,

    hhCheckState: "loading" as EvEligibilityCheckState,
    hhCheckHasStarted: false,

    carReg: "",
    registrationError: null as null | string,
    isCheckingRegistration: false,
    meterDialogKey: null as null | string,

    evOemSlug: null as null | string,
    evAssetId: null as null | string,
    chargerOemSlug: null as null | string,
    chargerAssetId: null as null | string,
    noCharger: false,
  }
}

const getDefaultState = () => {
  return {
    isWaitlistEnabled: false,
    isSmartChargingRenewalEnabled: false,

    ...getDefaultFormState(),

    evAssets: [] as SmartChargingCompatibleAsset[],
    chargerAssets: [] as SmartChargingCompatibleAsset[],
    assetCatalogueState: "idle" as AssetCatalogueState,

    isSmartChargingEligible: null as null | boolean,
    isSupportedSoon: null as null | boolean,
    eligibilityCheckId: null as null | number,
    isEligibilityChecked: false,

    hhCheckRun: 0,

    joinedWaitlistAccounts: getPersistedWaitlistJoins(),
    isJoiningWaitlist: false,
  }
}

const toOemOptions = (
  assets: SmartChargingCompatibleAsset[]
): SelectOption[] => {
  const bySlug = new Map<string, string>()
  assets.forEach(({ oem }) => bySlug.set(oem.slug, oem.displayName))

  return [
    ...[...bySlug]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    { value: OTHER_OPTION_VALUE, label: "Other" },
  ]
}

const toModelOptions = (
  assets: SmartChargingCompatibleAsset[],
  oemSlug: string | null
): SelectOption[] =>
  oemSlug
    ? [
        ...assets
          .filter((asset) => asset.oem.slug === oemSlug)
          .map(({ id, displayName }) => ({ value: id, label: displayName }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        { value: OTHER_OPTION_VALUE, label: "Other" },
      ]
    : []

const isSelectionComplete = (
  oemSlug: string | null,
  assetId: string | null
): boolean => !!oemSlug && (oemSlug === OTHER_OPTION_VALUE || !!assetId)

const commitLoadedAssets = (
  commit: (mutation: string, payload: unknown) => void,
  assets: SmartChargingCompatibleAsset[]
): void => {
  commit(
    "SET_EV_ASSETS",
    assets.filter((asset) => asset.type === "electric_vehicle")
  )
  commit(
    "SET_CHARGER_ASSETS",
    assets.filter((asset) => asset.type === "charger")
  )
  commit("SET_ASSET_CATALOGUE_STATE", "loaded")
}

const validateCarReg = (carReg: string): string | null => {
  if (!carReg) {
    return EvRegistrationCheckErrorMessages.CarRegistrationNoInputError
  }
  if (!/^[A-Za-z0-9 ]+$/.test(carReg)) {
    return EvRegistrationCheckErrorMessages.InvalidCharactersCarRegistrationError
  }
  if (carReg.length > MAX_VEHICLE_REGISTRATION_LENGTH) {
    return EvRegistrationCheckErrorMessages.CarRegistrationLengthError
  }
  return null
}

const errorCodeOf = (error): string =>
  error?.response?.data?.code ?? error?.message ?? ""

const reportFailure = (error): void => {
  console.error(error)
  ErrorReporter.report(error)
}

const readCachedAssets = (): SmartChargingCompatibleAsset[] | null => {
  const cached = sessionStore.getItem(COMPATIBLE_ASSETS_STORAGE_KEY)
  if (!cached) return null

  try {
    const assets = JSON.parse(cached)
    return Array.isArray(assets) && assets.length ? assets : null
  } catch (error) {
    sessionStore.removeItem(COMPATIBLE_ASSETS_STORAGE_KEY)
    return null
  }
}

export const state = getDefaultState()

export const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState(), {
      isWaitlistEnabled: state.isWaitlistEnabled,
      isSmartChargingRenewalEnabled: state.isSmartChargingRenewalEnabled,
    })
  },
  RESET_FORM(state) {
    Object.assign(state, getDefaultFormState())
    // Any half-hourly check still in flight belongs to the cleared form.
    state.hhCheckRun += 1
  },
  SET_WAITLIST_ENABLED(state, isWaitlistEnabled) {
    state.isWaitlistEnabled = isWaitlistEnabled
  },
  SET_SMART_CHARGING_RENEWAL_ENABLED(state, isSmartChargingRenewalEnabled) {
    state.isSmartChargingRenewalEnabled = isSmartChargingRenewalEnabled
  },
  SET_HAS_EV(state, hasEv) {
    state.hasEv = hasEv
    if (!hasEv) {
      state.evOemSlug = null
      state.evAssetId = null
    }
  },
  SET_IS_CONSIDERING_EV(state, isConsideringEv) {
    state.isConsideringEv = isConsideringEv
  },
  SET_HAS_ELIGIBLE_VEHICLE(state, hasEligibleVehicle) {
    state.hasEligibleVehicle = hasEligibleVehicle
  },
  SET_VEHICLE_FUEL_TYPE(state, vehicleFuelType) {
    state.vehicleFuelType = vehicleFuelType
  },
  SET_IS_METER_ELIGIBLE(state, isMeterEligible) {
    state.isMeterEligible = isMeterEligible
  },
  SET_HH_CHECK_STATE(state, hhCheckState: EvEligibilityCheckState) {
    state.hhCheckState = hhCheckState
  },
  SET_HH_CHECK_HAS_STARTED(state, hhCheckHasStarted) {
    state.hhCheckHasStarted = hhCheckHasStarted
  },
  SET_CAR_REG(state, carReg: string) {
    state.carReg = carReg
  },
  SET_REGISTRATION_ERROR(state, registrationError: string | null) {
    state.registrationError = registrationError
  },
  SET_IS_CHECKING_REGISTRATION(state, isCheckingRegistration: boolean) {
    state.isCheckingRegistration = isCheckingRegistration
  },
  SET_METER_DIALOG_KEY(state, meterDialogKey: string | null) {
    state.meterDialogKey = meterDialogKey
  },
  SET_EV_ASSETS(state, evAssets: SmartChargingCompatibleAsset[]) {
    state.evAssets = evAssets
  },
  SET_CHARGER_ASSETS(state, chargerAssets: SmartChargingCompatibleAsset[]) {
    state.chargerAssets = chargerAssets
  },
  SET_ASSET_CATALOGUE_STATE(state, assetCatalogueState: AssetCatalogueState) {
    state.assetCatalogueState = assetCatalogueState
  },
  SET_EV_OEM_SLUG(state, evOemSlug) {
    state.evOemSlug = evOemSlug
    state.evAssetId = null
    state.hasEv = true
  },
  SET_EV_ASSET_ID(state, evAssetId) {
    state.evAssetId = evAssetId
    state.hasEv = true
  },
  SET_CHARGER_OEM_SLUG(state, chargerOemSlug) {
    state.chargerOemSlug = chargerOemSlug
    state.chargerAssetId = null
    state.noCharger = false
  },
  SET_CHARGER_ASSET_ID(state, chargerAssetId) {
    state.chargerAssetId = chargerAssetId
    state.noCharger = false
  },
  SET_NO_CHARGER(state, noCharger) {
    state.noCharger = noCharger
    if (noCharger) {
      state.chargerOemSlug = null
      state.chargerAssetId = null
    }
  },
  SET_IS_SMART_CHARGING_ELIGIBLE(state, isSmartChargingEligible) {
    state.isSmartChargingEligible = isSmartChargingEligible
  },
  SET_IS_SUPPORTED_SOON(state, isSupportedSoon) {
    state.isSupportedSoon = isSupportedSoon
  },
  SET_ELIGIBILITY_CHECK_ID(state, eligibilityCheckId) {
    state.eligibilityCheckId = eligibilityCheckId
  },
  SET_IS_ELIGIBILITY_CHECKED(state, isEligibilityChecked) {
    state.isEligibilityChecked = isEligibilityChecked
  },
  SET_HAS_JOINED_WAITLIST(state, accountId) {
    state.joinedWaitlistAccounts = {
      ...state.joinedWaitlistAccounts,
      [accountId]: true,
    }
  },
  SET_IS_JOINING_WAITLIST(state, isJoiningWaitlist) {
    state.isJoiningWaitlist = isJoiningWaitlist
  },
}

export const getters = {
  isEligibleForEvTariff(state): boolean {
    return !!(
      state.hasEv &&
      state.hasEligibleVehicle &&
      state.isMeterEligible &&
      state.hhCheckState === "pass"
    )
  },
  isVehicleVerified(state): boolean {
    return !!(state.hasEligibleVehicle && state.isMeterEligible)
  },
  // Success is confirmed on the button itself, so only failures get a dialog.
  meterDialog(state) {
    if (
      !state.meterDialogKey ||
      state.meterDialogKey === MeterEligibilityError.SUCCESSFUL
    ) {
      return null
    }
    return SmValidationDialogs[state.meterDialogKey] ?? null
  },
  evOemOptions(state): SelectOption[] {
    return toOemOptions(state.evAssets)
  },
  evModelOptions(state): SelectOption[] {
    return toModelOptions(state.evAssets, state.evOemSlug)
  },
  chargerOemOptions(state): SelectOption[] {
    return toOemOptions(state.chargerAssets)
  },
  chargerModelOptions(state): SelectOption[] {
    return toModelOptions(state.chargerAssets, state.chargerOemSlug)
  },
  evSelectionComplete(state): boolean {
    return isSelectionComplete(state.evOemSlug, state.evAssetId)
  },
  chargerSelectionComplete(state): boolean {
    return (
      state.noCharger ||
      isSelectionComplete(state.chargerOemSlug, state.chargerAssetId)
    )
  },
  isSmartChargingJourneyEnabled(state): boolean {
    return state.isWaitlistEnabled || state.isSmartChargingRenewalEnabled
  },
  smartChargingEligibilityOutcome(
    state,
    getters
  ): SmartChargingEligibilityOutcome | null {
    // Without a check id there's nothing to offer — joining the waitlist needs one too
    if (
      !getters.isSmartChargingJourneyEnabled ||
      !state.isEligibilityChecked ||
      !state.eligibilityCheckId
    )
      return null
    if (state.isSmartChargingEligible) return "eligible"
    // Axle only flags a charger it can identify, so this is already false for a
    // customer with no charger or one we could take by name alone.
    if (state.isSupportedSoon) return "supportedSoon"

    return "notSupported"
  },
  isAssetCatalogueUnavailable(state): boolean {
    return state.assetCatalogueState === "error"
  },
  hasJoinedWaitlist(state, _getters, rootState): boolean {
    return state.joinedWaitlistAccounts[rootState.currentAccount] === true
  },
  smartChargingEligibilityCheckId(state): number | null {
    return state.eligibilityCheckId
  },
  canSubmit(state, getters): boolean {
    if (state.hasEv !== true || !getters.isEligibleForEvTariff) return false
    // Selects have nothing to offer without a catalogue, so requiring an answer
    // would leave an eligible customer unable to renew at all.
    if (getters.isAssetCatalogueUnavailable) return true

    return getters.evSelectionComplete && getters.chargerSelectionComplete
  },
  selectedEvAsset(state): SmartChargingCompatibleAsset | null {
    if (state.evOemSlug === OTHER_OPTION_VALUE) return null
    return state.evAssets.find((asset) => asset.id === state.evAssetId) ?? null
  },
  selectedChargerAsset(state): SmartChargingCompatibleAsset | null {
    if (state.noCharger || state.chargerOemSlug === OTHER_OPTION_VALUE)
      return null
    return (
      state.chargerAssets.find((asset) => asset.id === state.chargerAssetId) ??
      null
    )
  },
}

export const actions = {
  async joinWaitlist({ state, getters, commit, rootState }) {
    if (
      !state.eligibilityCheckId ||
      getters.hasJoinedWaitlist ||
      state.isJoiningWaitlist
    ) {
      return
    }

    commit("SET_IS_JOINING_WAITLIST", true)

    try {
      await GatewayPortalV2.joinSmartChargingWaitlist(
        rootState.currentAccount,
        {
          eligibilityCheckId: state.eligibilityCheckId,
        }
      )
      commit("SET_HAS_JOINED_WAITLIST", rootState.currentAccount)
      localStore.setItem(
        WAITLIST_JOINS_STORAGE_KEY,
        JSON.stringify(state.joinedWaitlistAccounts)
      )
      TrackingService.sendEvent(EV_SMART_WAITLIST_JOIN_TRACKING_EVENT, {
        outcome: "joined",
      })
      return true
    } catch (error) {
      TrackingService.sendEvent(EV_SMART_WAITLIST_JOIN_TRACKING_EVENT, {
        outcome: "error",
      })
      reportFailure(error)
      return false
    } finally {
      commit("SET_IS_JOINING_WAITLIST", false)
    }
  },
  setCarReg({ state, commit }, rawCarReg: string) {
    const carReg = (rawCarReg ?? "").replace(/\s+/g, "").toUpperCase()
    // A different plate has not been verified, so the check has to run again.
    if (carReg !== state.carReg) {
      commit("SET_HAS_ELIGIBLE_VEHICLE", null)
    }
    commit("SET_CAR_REG", carReg)
    commit("SET_REGISTRATION_ERROR", validateCarReg(carReg))
  },

  async submitRegistrationCheck({ state, commit, dispatch }) {
    const validationError = validateCarReg(state.carReg)
    commit("SET_REGISTRATION_ERROR", validationError)
    if (validationError) return

    commit("SET_IS_CHECKING_REGISTRATION", true)
    commit("SET_METER_DIALOG_KEY", null)

    try {
      await dispatch("verifyVehicleForEvTariffEligibility", state.carReg)
    } catch (e) {
      reportFailure(e)
      commit(
        "SET_REGISTRATION_ERROR",
        EvRegistrationCheckErrorMessages[errorCodeOf(e)] ??
          EvRegistrationCheckErrorMessages.NoValidEvFoundError
      )
      commit("SET_IS_CHECKING_REGISTRATION", false)
      return
    }

    try {
      await dispatch("verifyMeterForEvTariffEligibility")
      commit("SET_METER_DIALOG_KEY", MeterEligibilityError.SUCCESSFUL)
    } catch (e) {
      reportFailure(e)
      const errorCode = errorCodeOf(e)
      commit(
        "SET_METER_DIALOG_KEY",
        SmValidationDialogs[errorCode]
          ? errorCode
          : MeterEligibilityError.UNSUCCESSFUL
      )
    }

    commit("SET_IS_CHECKING_REGISTRATION", false)
  },

  async verifyVehicleForEvTariffEligibility(
    { commit, dispatch },
    carReg: string
  ) {
    if (isEvEligibilityBypassed()) {
      commit("SET_HAS_ELIGIBLE_VEHICLE", true)
      return
    }

    let data
    try {
      ;({ data } = await GatewayPortalV2.verifyEvVehicle(carReg))
    } catch (e) {
      TrackingService.sendEvent(EV_SMART_VEHICLE_ELIGIBILITY_TRACKING_EVENT, {
        outcome: "error",
      })
      throw e
    }

    const vehicleFuelType = data?.fuelType
    const isEligible = data?.isEv
    commit("SET_HAS_ELIGIBLE_VEHICLE", isEligible)
    if (vehicleFuelType) {
      await dispatch("updateVehicleFuelType", vehicleFuelType)
    }
    TrackingService.sendEvent(EV_SMART_VEHICLE_ELIGIBILITY_TRACKING_EVENT, {
      outcome: isEligible ? "pass" : "fail",
      fuelType: vehicleFuelType ?? "unknown",
    })
    if (!isEligible) {
      throw new Error(EvRegistrationCheckErrorMessages.NoValidEvFoundError)
    }
    return { vehicleFuelType }
  },

  async updateVehicleFuelType({ commit, rootState }, vehicleFuelType: string) {
    commit("SET_VEHICLE_FUEL_TYPE", vehicleFuelType)

    const customerId = rootState.user.customer.id
    const fuelType = EvFuelType[vehicleFuelType]

    try {
      if (!fuelType) throw new Error("Invalid vehicle fuel type")
      await GatewayPortalV2.updateCustomerDetails(customerId, { fuelType })
    } catch (e) {
      console.error(e)
      ErrorReporter.report(e)
    }
  },

  async verifyMeterForEvTariffEligibility({ commit, rootGetters }) {
    if (isEvEligibilityBypassed()) {
      commit("SET_IS_METER_ELIGIBLE", true)
      return
    }

    const mpan = rootGetters["smbp/mpan"]
    if (!mpan) {
      TrackingService.sendEvent(EV_SMART_METER_ELIGIBILITY_TRACKING_EVENT, {
        outcome: "fail",
        reason: MeterEligibilityError.NO_METER_POINT_FOUND,
      })
      throw new Error(MeterEligibilityError.NO_METER_POINT_FOUND)
    }

    let data
    try {
      ;({ data } = await GatewayPortalV2.verifySmartMeter(mpan))
    } catch (e) {
      TrackingService.sendEvent(EV_SMART_METER_ELIGIBILITY_TRACKING_EVENT, {
        outcome: "error",
      })
      throw e
    }

    const isMeterEligible = !!data?.isEligible
    commit("SET_IS_METER_ELIGIBLE", isMeterEligible)
    TrackingService.sendEvent(EV_SMART_METER_ELIGIBILITY_TRACKING_EVENT, {
      outcome: isMeterEligible ? "pass" : "fail",
      ...(data?.errorCode ? { reason: data.errorCode } : {}),
    })
    if (data?.errorCode) throw new Error(data.errorCode)
    // A bare rejection still has to reach the customer as a failure.
    if (!isMeterEligible) throw new Error(MeterEligibilityError.UNSUCCESSFUL)
    return { isMeterEligible }
  },

  async runHhEligibilityCheck({
    state,
    commit,
    dispatch,
    rootState,
    rootGetters,
  }) {
    if (state.hhCheckHasStarted) return
    commit("SET_HH_CHECK_HAS_STARTED", true)

    const run = state.hhCheckRun
    const isCurrentRun = () => run === state.hhCheckRun
    const commitCurrent = (mutation: string, payload: unknown) => {
      if (isCurrentRun()) commit(mutation, payload)
    }

    if (
      isEvEligibilityBypassed() ||
      process.env.VUE_APP_EV_RENEWAL_HH_ELIGIBILITY_CHECK_ENABLED !== "true"
    ) {
      commitCurrent("SET_HH_CHECK_STATE", "pass")
      return
    }

    commitCurrent("SET_HH_CHECK_STATE", "loading")

    const trackOutcome = (
      outcome: EvEligibilityCheckState,
      extra: Record<string, unknown> = {}
    ) =>
      TrackingService.sendEvent(EV_SMART_HH_ELIGIBILITY_TRACKING_EVENT, {
        outcome,
        ...extra,
      })

    try {
      const electricMeter = rootGetters["smbp/electricMeter"]
      const meterPointId = electricMeter?.meterPointId
      if (!meterPointId) {
        commitCurrent("SET_HH_CHECK_STATE", "error")
        commitCurrent("SET_HH_CHECK_HAS_STARTED", false)
        trackOutcome("error", { reason: "noMeterPoint" })
        return
      }

      if (rootState.meters.meterReadFrequency === undefined) {
        await dispatch("meters/fetchReadFrequency", null, { root: true })
      }
      const consents: ConsentEntry[] = rootState.meters.meterReadFrequency ?? []
      const consent = consents.find(
        (entry) =>
          entry["meter-point-id"]?.toString() === meterPointId.toString()
      )

      if (consent?.frequency !== MeterReadingFrequency["half-hourly"]) {
        commitCurrent("SET_HH_CHECK_STATE", "noConsent")
        commitCurrent("SET_HH_CHECK_HAS_STARTED", false)
        trackOutcome("noConsent")
        return
      }

      const now = dayjs()
      const { data } = await GatewayPortalV2.getEnergyUsageV2(
        rootState.currentAccount,
        {
          granularity: "HH",
          fromDate: now
            .subtract(EV_ELIGIBILITY_WINDOW_START_HOURS_AGO, "hour")
            .toISOString(),
          toDate: now
            .subtract(EV_ELIGIBILITY_WINDOW_END_HOURS_AGO, "hour")
            .toISOString(),
        }
      )

      const validReadCount = countValidHHSlots(
        data as EnergyUsageEntry[] | undefined,
        meterPointId
      )
      const dataAvailabilityPercent = Math.round(
        (validReadCount / EV_ELIGIBILITY_HH_SLOTS_PER_DAY) * 100
      )
      const outcome: EvEligibilityCheckState =
        validReadCount >= EV_ELIGIBILITY_REQUIRED_HH_READS
          ? "pass"
          : evNoHHDataState(electricMeter.supplyStartDate)

      commitCurrent("SET_HH_CHECK_STATE", outcome)
      trackOutcome(outcome, {
        hhReadsReceived: validReadCount,
        hhDataAvailabilityPercent: dataAvailabilityPercent,
        requiredHhReads: EV_ELIGIBILITY_REQUIRED_HH_READS,
      })
    } catch (e) {
      console.error(e)
      ErrorReporter.report(e)
      commitCurrent("SET_HH_CHECK_STATE", "error")
      commitCurrent("SET_HH_CHECK_HAS_STARTED", false)
      trackOutcome("error", { reason: "exception" })
    }
  },

  async submitEligibility({ state, getters, commit, dispatch, rootState }) {
    const evAsset = getters.selectedEvAsset
    const chargerAsset = getters.selectedChargerAsset
    const payload: Record<string, string> = {}

    if (evAsset) {
      payload.axleEvAssetId = evAsset.id
      payload.axleEvName = assetName(evAsset)
    } else if (getters.evSelectionComplete) {
      payload.axleEvName = OTHER_ASSET_NAME
    }

    if (chargerAsset) {
      payload.axleChargerAssetId = chargerAsset.id
      payload.axleChargerName = assetName(chargerAsset)
    } else if (state.noCharger) {
      payload.axleChargerName = NO_CHARGER_ASSET_NAME
    } else if (getters.chargerSelectionComplete) {
      payload.axleChargerName = OTHER_ASSET_NAME
    }

    if (Object.keys(payload).length) {
      try {
        const { data } = await GatewayPortalV2.checkSmartChargingEligibility(
          rootState.currentAccount,
          payload
        )
        commit("SET_ELIGIBILITY_CHECK_ID", data?.id ?? null)
        commit("SET_IS_SMART_CHARGING_ELIGIBLE", !!data?.isEligible)
        commit("SET_IS_SUPPORTED_SOON", !!data?.isSupportedSoon)
        TrackingService.sendEvent(
          EV_SMART_CHARGING_ELIGIBILITY_TRACKING_EVENT,
          {
            isEligible: !!data?.isEligible,
            isSupportedSoon: !!data?.isSupportedSoon,
            hasEv: !!evAsset,
            hasCharger: !!chargerAsset,
          }
        )
      } catch (e) {
        reportFailure(e)
      }
    }

    // An unsupported device never blocks the renewal, so the quotes are always
    // fetched and the check is always marked as done.
    await dispatch(
      "renew/fetchRenewalQuotes",
      {
        generateQuotesForEvTariffs: true,
        includeSmartChargingTariffs:
          state.isSmartChargingRenewalEnabled &&
          !!state.isSmartChargingEligible,
      },
      { root: true }
    )
    commit("SET_IS_ELIGIBILITY_CHECKED", true)
  },

  async fetchCompatibleAssets({ state, commit }) {
    if (
      state.assetCatalogueState === "loaded" ||
      state.assetCatalogueState === "loading"
    ) {
      return
    }

    const cachedAssets = readCachedAssets()
    if (cachedAssets) {
      commitLoadedAssets(commit, cachedAssets)
      return
    }

    commit("SET_ASSET_CATALOGUE_STATE", "loading")
    try {
      const { data } = await GatewayPortalV2.getSmartChargingCompatibleAssets()
      if (!Array.isArray(data) || !data.length) {
        throw new Error("Empty smart charging compatible asset catalogue")
      }
      const assets: SmartChargingCompatibleAsset[] = data
      commitLoadedAssets(commit, assets)
      sessionStore.setItem(
        COMPATIBLE_ASSETS_STORAGE_KEY,
        JSON.stringify(assets)
      )
    } catch (error) {
      commit("SET_EV_ASSETS", [])
      commit("SET_CHARGER_ASSETS", [])
      commit("SET_ASSET_CATALOGUE_STATE", "error")
      ErrorReporter.report(error)
    }
  },
}
